import { useContext, useEffect, useState } from 'react';
import { FetchContext, type ArticlePreview } from '@src/scenario-editor/providers/fetch.context';
import { getCachedArticlePreview, loadArticlePreview } from '@src/components/dynamicSelector/articlePreviewCache';
import { PreviewCard } from '@src/components/dynamicSelector/PreviewCard';

interface ArticlePreviewCardProps {
  id: string;
  title: string;
}

// LF360-2015: shown beside the picker for the hovered (or arrow-key) row. The
// title comes from the list at once; the excerpt, cover and chips come from
// one cached fetch per article.
export const ArticlePreviewCard = ({ id, title }: ArticlePreviewCardProps) => {
  const fetchArticlePreview = useContext(FetchContext)?.fetchArticlePreview;
  const [result, setResult] = useState<{ id: string; preview?: ArticlePreview; hasFailed?: boolean }>();

  useEffect(() => {
    if (!fetchArticlePreview || getCachedArticlePreview(id)) return;

    let isCancelled = false;
    loadArticlePreview(id, fetchArticlePreview)
      .then((preview) => {
        if (!isCancelled) setResult({ id, preview });
      })
      .catch((error) => {
        console.error('Error fetching article preview', error);
        if (!isCancelled) setResult({ id, hasFailed: true });
      });

    return () => {
      isCancelled = true;
    };
  }, [id, fetchArticlePreview]);

  const preview = getCachedArticlePreview(id) ?? (result?.id === id ? result.preview : undefined);
  const hasFailed = result?.id === id && !!result.hasFailed;
  const chips = [...(preview?.categories ?? []), ...(preview?.tags ?? [])];

  return (
    <PreviewCard title={preview?.title ? preview.title : title}>
      {preview?.imageUrl && (
        <img src={preview.imageUrl} alt="" className="mb-3 max-h-40 w-full rounded-sm object-cover"/>
      )}
      {!preview && !hasFailed && (
        <div className="space-y-2" aria-label="Loading preview">
          <div className="h-3 w-full animate-pulse rounded-sm bg-gray-100"/>
          <div className="h-3 w-11/12 animate-pulse rounded-sm bg-gray-100"/>
          <div className="h-3 w-2/3 animate-pulse rounded-sm bg-gray-100"/>
        </div>
      )}
      {hasFailed && <div className="text-label-foreground">The preview could not be loaded.</div>}
      {preview && !preview.excerpt && <div className="text-label-foreground">This article has no text.</div>}
      {preview?.excerpt && (
        <p className="whitespace-pre-line break-words font-light text-gray-700">{preview.excerpt}</p>
      )}
      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {chips.map((chip) => (
            <span key={chip} className="rounded-sm bg-gray-100 px-1.5 text-xs text-gray-600">{chip}</span>
          ))}
        </div>
      )}
    </PreviewCard>
  );
};
